/*
 * Cloud ingestion channels — the facts two unrelated screens must agree on.
 *
 * The Document Sources settings page and the Workflow Designer's Cloud Source
 * node both list channels and both describe channel health. Duplicating either
 * list is how they drift: a new non-cloud channel type added to one filter and
 * not the other would show up as a selectable "source" in the designer while
 * being correctly hidden on the settings page — and the workflow saved against
 * it would never receive a document.
 *
 * Lives under constants/ rather than being exported from the page, so neither
 * screen imports the other.
 */

/**
 * Channel types that are not cloud sources.
 *
 * manual_upload is a real `ingestion_channels` row and carries an
 * `enumeration_interval_seconds` because it is a column with a default — but
 * it is a person with a file, never enumerated on a cadence, so the number is
 * meaningless for it.
 *
 * folder_watch is RETIRED (the local folder watcher was removed 2026-10-05).
 * Its rows still exist, set inactive, and the server no longer lists them; the
 * name stays here so a row served by an older build still renders no card and
 * is never offered as a workflow source.
 *
 * FILTER BY TYPE, NOT BY REGISTRY MEMBERSHIP. Filtering on "has a provider
 * entry" looks equivalent and silently hides a real, working channel the day a
 * provider reaches the channel_type CHECK before it reaches the registry.
 *
 * Mirrored server-side in services/workflow/cloudSourceBindings.ts; the two
 * services share no code by policy, so this pair is duplicated on purpose.
 */
export const NON_CLOUD_CHANNEL_TYPES = ['manual_upload', 'folder_watch'];

/**
 * How each health state reads to a person.
 *
 * `degraded` is deliberately quiet — the poller is still retrying and the
 * customer has nothing to do, so an alarm would train them to ignore the one
 * state that does need them. `reconnect_needed` is that state.
 *
 * `orphaned` is NOT written by anything today (the binding decision revised it
 * out: it sits in the poller's UNPOLLED_STATES, so writing it would stop the
 * credential refresh on a channel an admin is mid-way through setting up).
 * "Not linked to a workflow" is rendered from the list's `workflow_id`
 * instead. The entry stays because the value remains legal in the CHECK.
 */
export const HEALTH_PRESENTATION = {
  connected: { tone: 'ok', label: 'Connected' },
  degraded: {
    tone: 'warn',
    label: 'Having trouble reaching this source — retrying automatically',
  },
  reconnect_needed: {
    tone: 'alarm',
    label: 'Connection lost. Documents are not arriving from this source.',
  },
  orphaned: { tone: 'neutral', label: 'Not linked to a workflow' },
};

/**
 * An unknown health value renders as itself, neutrally. NEVER HIDDEN: a state
 * this build has not heard of is exactly the case where an admin needs to see
 * something is off, and showing the raw string beats showing nothing while a
 * future migration ships.
 */
export function presentHealth(status) {
  if (status && HEALTH_PRESENTATION[status]) return HEALTH_PRESENTATION[status];
  return { tone: 'neutral', label: status ? String(status) : 'Unknown' };
}

/**
 * The channel types that watch a MAILBOX rather than a folder tree.
 *
 * A list rather than a `=== 'outlook_mailbox'` scattered through the page, for
 * the reason the header gives about the other list: Gmail follows the same shape
 * later, and the day it lands every comparison written as an equality is a
 * separate thing to find.
 */
export const MAIL_CHANNEL_TYPES = ['outlook_mailbox'];

export function isMailChannel(channelType) {
  return MAIL_CHANNEL_TYPES.includes(channelType);
}

/**
 * How a bound mailbox reads on the card.
 *
 * Every mailbox channel stores the address it reads. For 'self' that is the
 * connected Microsoft 365 account's own mailbox, and the card says so beside
 * the address: "mine" would be wrong for every admin except the one who
 * connected, and the address alone would not say whose it is.
 *
 * A 'self' row with no address is one from before connections, on a tenant that
 * has not connected yet. It gets the phrase without an address rather than a
 * blank.
 */
export function presentMailbox(channel) {
  if (!channel || !channel.mailbox_kind) return null;
  if (channel.mailbox_kind === 'self') {
    return channel.mailbox_address
      ? `${channel.mailbox_address} (the connected account)`
      : 'The connected account\u2019s mailbox';
  }
  return channel.mailbox_address || 'A shared mailbox';
}

/**
 * The connection's own wording for the same four states.
 *
 * The source labels above say "this source". On the Microsoft 365 card that is
 * the wrong noun twice over — it is not a source, and when it needs
 * reconnecting it is EVERY source that has stopped, which the card says in a
 * sentence of its own.
 */
export const CONNECTION_HEALTH_PRESENTATION = {
  connected: { tone: 'ok', label: 'Connected' },
  degraded: { tone: 'warn', label: 'Having trouble reaching Microsoft — retrying automatically' },
  reconnect_needed: { tone: 'alarm', label: 'Reconnect needed' },
  orphaned: { tone: 'alarm', label: 'Reconnect needed' },
};

/** Unknown values render as themselves, never hidden — the same rule as presentHealth. */
export function presentConnectionHealth(status) {
  if (status && CONNECTION_HEALTH_PRESENTATION[status]) return CONNECTION_HEALTH_PRESENTATION[status];
  return { tone: 'neutral', label: status ? String(status) : 'Unknown' };
}

/** The channel types that ride on the tenant's Microsoft 365 connection. */
export const M365_CHANNEL_TYPES = ['onedrive', 'sharepoint', 'outlook_mailbox'];

export function usesM365Connection(channelType) {
  return M365_CHANNEL_TYPES.includes(channelType);
}

/**
 * Can a source be added to, or polled on, this connection?
 *
 * MIRRORS isConnectionUsable in capture-service. null (not connected) and the
 * two states that need a person are not usable; 'degraded' is — it is being
 * retried, and a source added now starts working when it recovers.
 */
export function isConnectionUsable(connection) {
  if (!connection) return false;
  return connection.health_status !== 'reconnect_needed' && connection.health_status !== 'orphaned';
}

/**
 * The attachment types a mailbox channel may import, as checkboxes.
 *
 * MIRRORS the column default in 20260831_ingestion_outlook_mailbox.sql. The two
 * are duplicated across a service boundary on purpose (the same policy as
 * NON_CLOUD_CHANNEL_TYPES) — but note what is NOT here and why:
 *
 *   Office types are absent. The adapter records them as `skipped_office`
 *   ("needs rendering") rather than as an unknown type, and offering a checkbox
 *   for something no OCR path can read would be a switch that imports documents
 *   which then fail.
 *
 *   image/heic is absent. It was in the original default and was removed:
 *   capture-app has no HEIC decoder, so a HEIC attachment stores fine and then
 *   fails at OCR.
 *
 *   image/tiff is absent, removed 2026-08-31 for the same reason as HEIC.
 *   Intake now wraps images into a PDF so they can be viewed, split and
 *   published, and the wrapper (pdf-lib) has embedJpg and embedPng and nothing
 *   else. Nothing on the host can convert a TIFF either — no ImageMagick, no
 *   libtiff-tools, no ghostscript, no PIL. A TIFF fax would be the one accepted
 *   attachment that still reaches the viewer as raw bytes and fails to render.
 *   Re-add it WITH a decoder, not before one.
 *
 * The server accepts any well-formed MIME type, so this list is what we OFFER,
 * not what is permitted. A channel configured by hand with something else keeps
 * it — see the note where these are rendered.
 */
export const ACCEPTED_MIME_TYPE_OPTIONS = [
  { value: 'application/pdf', label: 'PDF' },
  { value: 'image/jpeg', label: 'JPEG' },
  { value: 'image/png', label: 'PNG' },
];
